import {Component, EventEmitter, Input, OnInit, Output, inject} from '@angular/core';
import {DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {DialogService} from "../../../shared/services/dialog.service";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";

import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {UploaderModule} from "@syncfusion/ej2-angular-inputs";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {LinkComponent} from "../../../shared/elements/link/link.component";
import {ContentComponent} from "../../../shared/elements/content/content.component";
import {ProgressComponent} from "../../../shared/elements/progress/progress.component";
import {DocumentService} from "../../../shared/services/document.service";
import {LanguageService} from "../../../shared/services/language.service";
import {ToasterService} from "../../../shared/services/toaster.service";
import {TYPE} from "../../../shared/types/types";
import {FormLabelComponent} from "../../../shared/elements/form-label/form-label.component";
import {DialogDocumentsCommanderComponent} from "../dialog-documents-commander/dialog-documents-commander.component";
import {ItemDocumentsPreviewSelectedComponent} from "../item-documents-preview-selected/item-documents-preview-selected.component";
import {SettingsMenuItemType} from '../../../shared/types/settings-types/settingsMenuItemType';
import {Router} from '@angular/router';

@Component({
    selector: 'app-dialog-documents-upload',
    imports: [
        DialogModule,
        FormButtonComponent,
        HeadlineComponent,
        TooltipModule,
        TranslatePipe,
        UploaderModule,
        TooltipComponent,
        IconBadgeComponent,
        LinkComponent,
        ContentComponent,
        ProgressComponent,
        DialogDocumentsCommanderComponent,
        ItemDocumentsPreviewSelectedComponent,
        FormLabelComponent
    ],
    templateUrl: './dialog-documents-upload.component.html',
    styleUrl: './dialog-documents-upload.component.scss'
})
export class DialogDocumentsUploadComponent implements OnInit {
  public dialogService: DialogService = inject(DialogService);
  public documentService: DocumentService = inject(DocumentService);
  private toasterService: ToasterService = inject(ToasterService);
  private languageService: LanguageService = inject(LanguageService);
  private router: Router = inject(Router);

  @Input() public visible: boolean = false;
  @Input() public title: string = '';
  @Input() public tooltip: string = '';
  @Input() public description: string = '';
  @Input() public multiple: boolean = false;
  @Input() public allowExtensions: string[] = [];
  @Input() public view: 'dialog' | 'component' = 'dialog';
  @Input() public type: 'image' | 'media' | 'attachment' | 'both' = 'both';
  @Input() public requireFiles: boolean = false;
  @Input() public files: TYPE.Document[] = [];
  @Input() public hasError: boolean = false;
  @Input() public disabled: boolean = false;
  @Input() public uploadFolder: string = 'intranet';
  @Input() public uploadModule: string = '';
  @Input() public templateModule: SettingsMenuItemType | '' = '';
  @Input() public hideUploadedContent: boolean = false;
  @Input() public hideMyFilesOption: boolean = false;
  @Output() public visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public documentsUploaded: EventEmitter<TYPE.Document[]> = new EventEmitter<TYPE.Document[]>();
  @Output() public filesChange: EventEmitter<TYPE.Document[]> = new EventEmitter<TYPE.Document[]>();

  public totalProgress: number = 0;
  public uploadingFiles: TYPE.Document[] = [];
  public documentsCommanderDialog: boolean = false;
  private allowedMediaTypes: string[] = [];
  private forbiddenTypes: string[] = [];
  public isDraggingOver: boolean = false;
  public resolvedTemplateModule: SettingsMenuItemType | '' = '';
  public canOpenDocumentsCommander: boolean = true;

  public ngOnInit(): void {
    this.resolvedTemplateModule = this.templateModule || this.mapRouteToModule(this.router.url);
    this.canOpenDocumentsCommander = this.resolvedTemplateModule !== SettingsMenuItemType.Chat
      && this.resolvedTemplateModule !== SettingsMenuItemType.Profile;
    if(this.type === 'image') {
      this.allowedMediaTypes = this.documentService.allowedImageTypes;
      this.forbiddenTypes = [...this.documentService.allowedVideoTypes, ...this.documentService.allowedAudioTypes];
    } else {
      this.allowedMediaTypes = this.type === 'media' ? this.documentService.allowedMediaTypes : [];
      this.forbiddenTypes = this.type === 'attachment' ? this.documentService.allowedMediaTypes : [];
    }
  }

  public close(): void {
    this.visibleChange.emit(false);
  }

  public save(): void {
    this.documentsUploaded.emit(this.files.filter((file) => !file.error));
    this.close();
  }

  private uploadDocuments(documents: FileList): void {
    this.documentService.uploadDocuments(this.uploadFolder, documents, this.multiple, this.allowedMediaTypes, this.forbiddenTypes, this.uploadModule).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      next: (filesWithStatus: any) => {
        this.uploadingFiles = this.getSuccessfulFiles(filesWithStatus);
        this.updateProgress();
      }, complete: () => {
        const successfulFiles = this.getSuccessfulFiles(this.uploadingFiles);
        if (this.multiple) {
          this.files = [...successfulFiles, ...this.files];
        } else {
          this.files = successfulFiles;
        }
        this.uploadingFiles = [];
        this.filesChange.emit(this.files);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, error: (error: any) => {
        this.toasterService.error(this.languageService.getTranslationByKey('DOCUMENTS'), this.languageService.getTranslationByKey(error.message));
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public fileSelect(event: any): void {
    const documents = event.target.files as FileList | undefined;
    if (documents !== undefined) {
      this.uploadDocuments(documents);
    }
  }

  public fileDrop(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled) {
      this.isDraggingOver = false;
      const documents = event.dataTransfer?.files as FileList | undefined;
      if (documents !== undefined) {
        this.uploadDocuments(documents);
      }
    }
  }

  public dragOver(event: DragEvent): void {
    event.preventDefault();
    if (!this.disabled) {
      this.isDraggingOver = true;
    }
  }

  public onDragLeave(): void {
    if (!this.disabled) {
      this.isDraggingOver = false;
    }
  }

  private updateProgress(): void {
    this.totalProgress = Math.round(this.uploadingFiles.reduce((total, file) => total + (file.progress ? file.progress : 100), 0) / this.uploadingFiles.length);
    if (this.totalProgress === 100) {
      this.totalProgress = 0;
    }
  }

  public update(): void {
    this.filesChange.emit(this.files);
  }

  private getSuccessfulFiles(files: TYPE.Document[]): TYPE.Document[] {
    return files.filter((file) => !file.error);
  }

  public cancelUpload(): void {
    this.documentService.cancelUploads();
    this.uploadingFiles = [];
    this.totalProgress = 0;
  }

  public openDocumentsCommander(): void {
    if (!this.canOpenDocumentsCommander) {
      return;
    }
    this.documentsCommanderDialog = true;
  }

  private mapRouteToModule(url: string): SettingsMenuItemType | '' {
    const route = (url.split('?')[0].split('/')[1] || '').toLowerCase();
    const byRoute: Record<string, SettingsMenuItemType> = {
      chat: SettingsMenuItemType.Chat,
      calendar: SettingsMenuItemType.Calendar,
      contacts: SettingsMenuItemType.Contacts,
      community: SettingsMenuItemType.Communities,
      feedback: SettingsMenuItemType.Feedback,
      knowledgebase: SettingsMenuItemType.Knowledgebase,
      news: SettingsMenuItemType.News,
      meetings: SettingsMenuItemType.Meetings,
      profile: SettingsMenuItemType.Profile,
      settings: SettingsMenuItemType.Settings,
      tv: SettingsMenuItemType.Tv,
      workflow: SettingsMenuItemType.Workflow
    };

    return byRoute[route] ?? '';
  }
}
